import { useRef, useState } from 'react';
import { TabContent, TabPane, UncontrolledTooltip } from 'reactstrap';
import { useQueryClient } from '@tanstack/react-query';
import { NSBadge, NSNavTabs, NSSlideout } from 'bricks';
import { INSNavItem } from 'bricks/NSNavTabs/NSNavTabs';
import useToast from 'hooks/useToast';
import { useGetOrganizationUsers } from 'hooks/useGetOrganizationUsers';
import { useAssignUser } from 'hooks/useAssignUser';
import { useUnassignUser } from 'hooks/useUnassignUser';
import { TimelineAssignee } from 'types/timeline';
import { useUpdateTimeline } from 'views/DealDashboard/Overview/hooks/useUpdateTimeline';
import { TimelineDraft, TimelineItem, UpdateTimelinePayload } from '../../types';
import TimelineSlideoutHeader from './TimelineSlideoutHeader/TimelineSlideoutHeader';
import TimelineSlideoutDetails from './TimelineSlideoutDetails/TimelineSlideoutDetails';
import TimelineSlideoutComments from './TimelineSlideoutComments/TimelineSlideoutComments';

type TimelineSlideoutProps = {
    isOpen: boolean;
    timeline: TimelineItem | null;
    dealId: number;
    orgId: string;
    editMode: boolean;
    setEditMode: (editMode: boolean) => void;
    onClose: () => void;
    onSwitchTimeline: (id: string) => void;
    onDuplicate: (timeline: TimelineItem) => void;
    onRequestDelete: (timeline: TimelineItem) => void;
};

const DETAILS_TAB = 'details';
const COMMENTS_TAB = 'comments';
const COMMENTS_TAB_ID = 'timeline-slideout-comments-tab';
const ATTACHMENTS_TAB_ID = 'timeline-slideout-attachments-tab';
const COMING_SOON_TOOLTIP = 'Coming soon !';

const buildDraft = (timeline: TimelineItem): TimelineDraft => ({
    name: timeline.name,
    target: timeline.target,
    completed: timeline.completed,
    description: timeline.description,
    assignees: timeline.assignees ?? [],
});

export const TimelineSlideout = ({
    isOpen, timeline, dealId, orgId, editMode, setEditMode, onClose, onDuplicate, onRequestDelete,
}: TimelineSlideoutProps) => {
    const queryClient = useQueryClient();
    const { showSuccess, showError } = useToast();
    const { data: organizationUsers } = useGetOrganizationUsers(orgId, { enabled: isOpen && editMode });

    const numericTimelineId = timeline ? Number(timeline.id) : -1;
    const { mutate: updateTimeline } = useUpdateTimeline(() => {});
    const { mutateAsync: assignUser } = useAssignUser({ orgId, dealId, timelineId: numericTimelineId });
    const { mutateAsync: unassignUser } = useUnassignUser({ orgId, dealId, timelineId: numericTimelineId });

    const [activeTab, setActiveTab] = useState(DETAILS_TAB);
    const [initializedTimelineId, setInitializedTimelineId] = useState<string | null>(null);
    const [draft, setDraft] = useState<TimelineDraft | null>(null);
    const draftAssigneeIdRef = useRef(-1);

    const currentId = timeline?.id ?? null;
    if (currentId !== initializedTimelineId) {
        setInitializedTimelineId(currentId);
        setDraft(timeline ? buildDraft(timeline) : null);
    }

    if (!timeline || !draft) return null;

    const invalidateTimelines = () => queryClient.invalidateQueries({ queryKey: ['timelines', orgId, dealId] });

    const handleFieldChange = (patch: Partial<TimelineDraft>) =>
        setDraft(prev => (prev ? { ...prev, ...patch } : prev));

    const handleAssign = (userCorrelationId: string) => {
        setDraft((prev) => {
            if (!prev || prev.assignees.some(a => a.userCorrelationId === userCorrelationId)) {
                return prev;
            }

            const user = organizationUsers?.find(u => u.correlationId === userCorrelationId);
            const newAssignee: TimelineAssignee = {
                id: draftAssigneeIdRef.current,
                timelineId: numericTimelineId,
                userCorrelationId,
                createdAt: '',
                createdBy: '',
                userName: user?.name || user?.email || 'Unknown',
            };
            
            draftAssigneeIdRef.current -= 1;

            return { ...prev, assignees: [...prev.assignees, newAssignee] };
        });
    };

    const handleRemove = (userCorrelationId: string) =>
        setDraft(prev => (prev
            ? { ...prev, assignees: prev.assignees.filter(a => a.userCorrelationId !== userCorrelationId) }
            : prev));

    const handleCancel = () => {
        setDraft(buildDraft(timeline));
        setEditMode(false);
    };

    const buildChangedFields = (): UpdateTimelinePayload => {
        const changed: UpdateTimelinePayload = {};
        const trimmedName = draft.name.trim();
        if (trimmedName && trimmedName !== timeline.name) changed.name = trimmedName;
        if (draft.target !== timeline.target) changed.target = draft.target;
        if (draft.completed !== timeline.completed) changed.completed = draft.completed;
        if ((draft.description ?? '') !== (timeline.description ?? '')) changed.description = draft.description || null;
        return changed;
    };

    const getAssigneeDelta = () => {
        const originalIds = new Set((timeline.assignees ?? []).map(a => a.userCorrelationId));
        const draftIds = new Set(draft.assignees.map(a => a.userCorrelationId));
        return {
            toAdd: [...draftIds].filter(id => !originalIds.has(id)),
            toRemove: [...originalIds].filter(id => !draftIds.has(id)),
        };
    };

    const handleSave = async () => {
        const changed = buildChangedFields();
        const { toAdd, toRemove } = getAssigneeDelta();

        try {
            await Promise.all([
                ...toAdd.map(userCorrelationId => assignUser({ userCorrelationId })),
                ...toRemove.map(userCorrelationId => unassignUser(userCorrelationId)),
            ]);
        } catch {
            showError('Failed to update activity');
            return;
        }

        if (Object.keys(changed).length > 0) {
            updateTimeline(
                { orgId, dealId, timelineId: timeline.id, patchData: changed },
                {
                    onSuccess: () => { invalidateTimelines(); showSuccess('Activity updated'); setEditMode(false); },
                    onError: () => showError('Failed to update activity'),
                },
            );
        } else {
            invalidateTimelines();
            showSuccess('Activity updated');
            setEditMode(false);
        }
    };

    const navItems: INSNavItem[] = [
        {
            id: 'timeline-slideout-details-tab',
            name: 'Details',
            isActive: activeTab === DETAILS_TAB,
            callback: () => setActiveTab(DETAILS_TAB),
        },
        {
            id: COMMENTS_TAB_ID,
            name: 'Comments',
            isActive: activeTab === COMMENTS_TAB,
            callback: () => setActiveTab(COMMENTS_TAB),
        },
        {
            id: ATTACHMENTS_TAB_ID,
            name: 'Attachments',
            isActive: false,
            callback: () => {},
            className: 'cursor--disabled',
            badge: <NSBadge color="primary-lighten" className="ml-1">0</NSBadge>,
        },
    ];

    return (
        <NSSlideout
            isOpen={isOpen}
            toggle={onClose}
            size="lg"
            contentClassName="TimelineSlideout"
            modalBodyClassName="p-0"
            customHeader={(
                <TimelineSlideoutHeader
                    name={timeline.name}
                    editMode={editMode}
                    isDetailsTab={activeTab === DETAILS_TAB}
                    toggle={onClose}
                    onEdit={() => setEditMode(true)}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onDuplicate={() => onDuplicate(timeline)}
                    onDelete={() => onRequestDelete(timeline)}
                />
            )}
        >
            <NSNavTabs navItems={navItems} />
            <UncontrolledTooltip target={ATTACHMENTS_TAB_ID}>{COMING_SOON_TOOLTIP}</UncontrolledTooltip>

            <TabContent activeTab={activeTab}>
                <TabPane tabId={DETAILS_TAB}>
                    <TimelineSlideoutDetails
                        key={timeline.id}
                        timeline={timeline}
                        editMode={editMode}
                        draft={draft}
                        orgId={orgId}
                        onFieldChange={handleFieldChange}
                        onAssign={handleAssign}
                        onRemove={handleRemove}
                    />
                </TabPane>
                <TabPane tabId={COMMENTS_TAB}>
                    <TimelineSlideoutComments
                        key={timeline.id}
                        orgId={orgId}
                        dealId={dealId}
                        timelineId={numericTimelineId}
                        enabled={activeTab === COMMENTS_TAB}
                    />
                </TabPane>
            </TabContent>
        </NSSlideout>
    );
};

export default TimelineSlideout;
